import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useEffectiveWidth from "../hooks/useEffectiveWidth";

const PAGE_HEADER_BREAKPOINT_PX = 1400;

const TAB_UNDERLINE_STYLE = {
  height: "2px",
  minHeight: "2px",
  maxHeight: "2px",
  bottom: "-1px",
};

const PageHeader = ({
  title,
  titleContent = null,
  titleContentMain = null,
  titleContentBadges = null,
  subtitle = null,
  /** When true, title is not single-line truncated (long headings, e.g. confrontation detail) */
  titleMultiline = false,
  showBackButton = false,
  backRoute = "/",
  backLabel = null,
  onBackClick = null,
  tabs = [],
  activeTab,
  onTabChange,
  shareButton = null,
  actions = null,
  actionsMenuItems = null,
  /** Bouton "Comprendre cette page" — même cluster droit que les actions */
  helpButton = null,
  /** Aligne titre / retour sur une colonne centrée (ex. wizard 640px) */
  contentMaxWidth = null,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const effectiveWidth = useEffectiveWidth();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef(null);
  const actionsButtonRef = useRef(null);

  const isNarrow = effectiveWidth <= PAGE_HEADER_BREAKPOINT_PX;
  const hasActionsArea = actions || shareButton || helpButton;
  const hasActionsDropdown =
    Array.isArray(actionsMenuItems) && actionsMenuItems.length > 0;
  /** Sur grand écran, priorité au bouton direct (`actions` ou `shareButton`) si fourni. */
  const hasDirectAction = Boolean(actions || shareButton);
  const showActionsAsDropdown =
    hasActionsDropdown && (isNarrow || !hasDirectAction);

  const handleBackClick = () => {
    if (onBackClick && typeof onBackClick === "function") {
      onBackClick();
    } else {
      history.push(backRoute);
    }
  };

  useEffect(() => {
    if (!isActionsMenuOpen) return;
    const handleClickOutside = (e) => {
      if (
        actionsMenuRef.current?.contains(e.target) ||
        actionsButtonRef.current?.contains(e.target) ||
        e.target.closest?.(".page-header-actions-menu-portal")
      ) {
        return;
      }
      setIsActionsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActionsMenuOpen]);

  const updateActionsMenuPosition = () => {
    if (!actionsButtonRef.current || !actionsMenuRef.current) return;
    const rect = actionsButtonRef.current.getBoundingClientRect();
    const menuEl = actionsMenuRef.current;
    menuEl.style.top = `${rect.bottom + 8}px`;
    menuEl.style.left = `${rect.right}px`;
    menuEl.style.transform = "translateX(-100%)";
    menuEl.style.visibility = "visible";
  };

  useLayoutEffect(() => {
    if (!isActionsMenuOpen) return;
    updateActionsMenuPosition();
    const raf = requestAnimationFrame(updateActionsMenuPosition);
    window.addEventListener("resize", updateActionsMenuPosition);
    window.addEventListener("scroll", updateActionsMenuPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateActionsMenuPosition);
      window.removeEventListener("scroll", updateActionsMenuPosition, true);
    };
  }, [isActionsMenuOpen]);

  const openActionsMenu = () => {
    if (!hasActionsDropdown) return;
    setIsActionsMenuOpen((prev) => !prev);
  };

  const contentConstraintStyle = contentMaxWidth
    ? {
        maxWidth: contentMaxWidth,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
      }
    : undefined;

  const renderActionsMenuPortal = () => {
    if (!isActionsMenuOpen) return null;
    return createPortal(
      <div
        ref={actionsMenuRef}
        className="page-header-actions-menu-portal fixed bg-white rounded-lg border border-[#e0e0e0] py-1 min-w-[200px] max-w-[360px] z-[10000] flex flex-col"
        style={{
          visibility: "hidden",
          top: 0,
          left: 0,
          transformOrigin: "top right",
        }}
      >
        {actionsMenuItems.map((item, index) => (
          <button
            key={index}
            type="button"
            className="w-full px-4 py-2.5 text-left hover:bg-[#f5f5f5] text-sm text-[#252733] transition-colors first:rounded-t-lg last:rounded-b-lg border-0 bg-transparent cursor-pointer flex items-center gap-2 whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              item.onClick?.(e);
              setIsActionsMenuOpen(false);
            }}
          >
            {item.icon && (
              <span className="shrink-0 w-5 flex items-center justify-center [&>img]:w-4 [&>img]:h-4">
                {item.icon}
              </span>
            )}
            <span className="min-w-0">{item.label}</span>
          </button>
        ))}
      </div>,
      document.body,
    );
  };

  const renderActionsDropdown = () => (
    <>
      <div className="relative shrink-0 flex items-center h-10">
        <button
          ref={actionsButtonRef}
          type="button"
          onClick={openActionsMenu}
          aria-expanded={isActionsMenuOpen}
          aria-haspopup="menu"
          className="flex items-center gap-2 h-10 px-4 rounded-full border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors text-sm font-semibold text-[#222222] cursor-pointer"
        >
          <span>{t("Actions")}</span>
          <i className="fas fa-chevron-down text-xs" aria-hidden />
        </button>
      </div>
      {renderActionsMenuPortal()}
    </>
  );

  const renderRightCluster = (actionSlot, { includeHelp = true } = {}) => {
    const help = includeHelp ? helpButton : null;
    if (!help && !actionSlot) return null;
    return (
      <div className="shrink-0 flex items-center gap-3">
        {help}
        {actionSlot}
      </div>
    );
  };

  const renderTabs = () =>
    tabs.length > 0 ? (
      <div
        className="w-full overflow-x-auto overflow-y-hidden"
        style={{
          marginTop: "0",
          WebkitOverflowScrolling: "touch",
          scrollbarGutter: "stable",
        }}
      >
        <div className="flex gap-6 relative min-w-min">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className="relative pb-2.5 cursor-pointer shrink-0"
            >
              <span
                className={`font-mulish text-sm transition-colors whitespace-nowrap inline-flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? "text-[#00A7A6] font-medium"
                    : "text-[#222222] hover:text-[#00A7A6]"
                }`}
              >
                {tab.icon}
                {tab.label}
              </span>
              {activeTab === tab.id && (
                <div
                  className="absolute left-0 right-0 bg-[#00A7A6]"
                  style={TAB_UNDERLINE_STYLE}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    ) : null;

  const hasNarrowTitleSplit =
    isNarrow && titleContentMain != null && titleContentBadges != null;

  const titleLineClass = titleMultiline
    ? "text-[#222222] font-semibold leading-tight whitespace-normal break-words"
    : "text-[#222222] font-semibold leading-tight truncate";

  const wideTitleContent =
    titleContent ??
    (titleContentBadges ? (
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="min-w-0">{title}</span>
        {titleContentBadges}
      </div>
    ) : null);

  const renderWideTitle = (extraStyle = {}) => {
    if (wideTitleContent) {
      return (
        <div
          className={`${titleLineClass} min-w-0 flex-1`}
          style={{ fontSize: "2.2rem", ...extraStyle }}
        >
          {wideTitleContent}
        </div>
      );
    }
    if (title) {
      return (
        <h2
          className={`${titleLineClass} min-w-0 flex-1`}
          style={{ fontSize: "2.2rem", ...extraStyle }}
        >
          {title}
        </h2>
      );
    }
    return null;
  };

  const titleBlock = (title ||
    titleContent ||
    titleContentBadges ||
    subtitle) && (
    <>
      {(title || titleContent || titleContentBadges) &&
        renderWideTitle({
          marginBottom: helpButton || tabs.length > 0 ? 0 : "0.5rem",
        })}
      {subtitle && (
        <p
          className="text-[#6B7280] text-sm font-normal"
          style={{ marginTop: "4px", marginBottom: "12px" }}
        >
          {subtitle}
        </p>
      )}
    </>
  );

  const titleBlockNarrowSplit = hasNarrowTitleSplit && (
    <>
      <div
        className="text-[#222222] font-semibold leading-tight w-full truncate"
        style={{
          fontSize: "2.2rem",
          marginBottom: "0.75rem",
        }}
      >
        {titleContentMain}
      </div>
      <div
        className="w-full flex flex-wrap items-center gap-3"
        style={{
          marginTop: "0.25rem",
          marginBottom: subtitle
            ? "0.5rem"
            : tabs.length === 0
              ? "1rem"
              : "1rem",
        }}
      >
        {titleContentBadges}
      </div>
      {subtitle && (
        <p
          className="text-[#6B7280] text-sm font-normal"
          style={{ marginTop: "4px", marginBottom: "12px" }}
        >
          {subtitle}
        </p>
      )}
    </>
  );

  const backArrowSvg = (
    <svg
      width="16"
      height="12"
      viewBox="0 0 16 12"
      fill="none"
      className="shrink-0"
    >
      <path
        d="M6 12L0 6L6 0L7.4 1.45L3.85 5H16V7H3.85L7.4 10.55L6 12Z"
        fill="#222222"
      />
    </svg>
  );

  if (isNarrow) {
    /** Pas de dropdown mais un bouton direct fourni : on le garde visible sur écran étroit. */
    const narrowDirectAction =
      !showActionsAsDropdown && hasDirectAction ? actions ?? shareButton : null;
    const showTopRow =
      showBackButton || showActionsAsDropdown || Boolean(narrowDirectAction);
    const helpInTopRow = Boolean(showTopRow && helpButton);
    const narrowActionSlot = showActionsAsDropdown
      ? renderActionsDropdown()
      : narrowDirectAction;
    const narrowRightCluster = renderRightCluster(narrowActionSlot, {
      includeHelp: helpInTopRow,
    });
    return (
      <div
        className="w-full bg-white border-b border-[#D3D3D3] flex flex-col"
        style={{
          paddingLeft: "35px",
          paddingRight: "35px",
          paddingTop: "0.5rem",
          marginTop: "0rem",
        }}
      >
        {showTopRow && (
          <div className="flex items-center justify-between w-full pt-1 pb-2">
            {showBackButton ? (
              <button
                type="button"
                onClick={handleBackClick}
                className="w-10 h-10 shrink-0 rounded-full border border-[#D3D3D3] bg-white flex items-center justify-center hover:bg-[#f5f5f5] transition-colors cursor-pointer"
                aria-label={backLabel || t("Back")}
              >
                {backArrowSvg}
              </button>
            ) : (
              <div className="w-10 shrink-0" />
            )}
            <div className="shrink-0 flex items-center gap-3">
              {narrowRightCluster}
              {!narrowRightCluster && <div className="w-10 shrink-0" />}
            </div>
          </div>
        )}
        <div
          className="w-full min-w-0 max-w-full flex items-center justify-between gap-3 py-2"
          style={contentConstraintStyle}
        >
          <div className="min-w-0 flex-1 text-left overflow-hidden">
            {hasNarrowTitleSplit ? titleBlockNarrowSplit : titleBlock}
          </div>
          {!helpInTopRow &&
            renderRightCluster(null, { includeHelp: Boolean(helpButton) })}
        </div>
        {renderTabs()}
      </div>
    );
  }

  const hasDesktopTitleRow =
    title ||
    titleContent ||
    titleContentBadges ||
    showActionsAsDropdown ||
    hasActionsArea;

  const desktopActionSlot = showActionsAsDropdown
    ? renderActionsDropdown()
    : actions ?? shareButton;

  return (
    <div
      className="w-full bg-white border-b border-[#D3D3D3] flex flex-col"
      style={{
        paddingLeft: "35px",
        paddingRight: "35px",
        paddingTop: "0.5rem",
        marginTop: "0rem",
      }}
    >
      <div className="py-2" style={contentConstraintStyle}>
        {showBackButton && (
          <div
            onClick={handleBackClick}
            className="flex items-center gap-2 mb-3 cursor-pointer hover:opacity-70 transition-opacity w-fit"
          >
            {backArrowSvg}
            <span className="font-mulish text-sm text-[#222222]">
              {backLabel || t("Back")}
            </span>
          </div>
        )}
        {hasDesktopTitleRow && (
          <div
            className="flex items-center justify-between gap-3 min-w-0"
            style={{
              marginBottom: subtitle
                ? undefined
                : tabs.length > 0
                  ? 0
                  : "0.5rem",
            }}
          >
            {(title || titleContent || titleContentBadges) && renderWideTitle()}
            {(showActionsAsDropdown || hasActionsArea) &&
              renderRightCluster(desktopActionSlot)}
          </div>
        )}
        {subtitle && (
          <p
            className="text-[#6B7280] text-sm font-normal"
            style={{ marginTop: "4px", marginBottom: "12px" }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {renderTabs()}
    </div>
  );
};

export default PageHeader;
